/**
 * Detects actionable suggestions from chatbot responses
 */
export interface ActionSuggestion {
  id: string
  label: string
  type: 'navigation' | 'action' | 'link'
  action: string
  metadata?: Record<string, any>
}
/**
 * Analyze message content and extract actionable suggestions
 */
export function detectActionSuggestions(
  content: string,
  context?: {
    accountId?: string
    accountName?: string
  },
): ActionSuggestion[] {
  const suggestions: ActionSuggestion[] = []
  const lowerContent = content.toLowerCase()
  // Detect orders/jobs mentions
  if (
    lowerContent.match(/\b(order|job|project|campaign|quote)s?\b/i) &&
    (lowerContent.includes('view') ||
      lowerContent.includes('check') ||
      lowerContent.includes('review') ||
      lowerContent.includes('active') ||
      lowerContent.includes('pending'))
  ) {
    suggestions.push({
      id: 'view-orders',
      label: 'View Orders',
      type: 'navigation',
      action: 'navigate:orders',
    })
  }
  // Detect team/people mentions
  if (
    lowerContent.match(
      /\b(team|people|contact|member|colleague|person|staff)\b/i,
    ) &&
    (lowerContent.includes('view') ||
      lowerContent.includes('see') ||
      lowerContent.includes('check') ||
      lowerContent.includes('meet') ||
      lowerContent.includes('talk'))
  ) {
    suggestions.push({
      id: 'view-people',
      label: 'Show Team',
      type: 'navigation',
      action: 'navigate:people',
    })
  }
  // Detect account details mentions
  if (
    context?.accountName &&
    (lowerContent.includes('account') ||
      lowerContent.includes(context.accountName.toLowerCase())) &&
    (lowerContent.includes('detail') ||
      lowerContent.includes('overview') ||
      lowerContent.includes('information') ||
      lowerContent.includes('view'))
  ) {
    suggestions.push({
      id: 'view-account',
      label: 'Open Account Details',
      type: 'navigation',
      action: 'navigate:account',
      metadata: { accountId: context.accountId },
    })
  }
  // Detect Knowledge Space mentions
  if (
    lowerContent.match(/\b(knowledge space|space|document|resource|file)\b/i) &&
    (lowerContent.includes('open') ||
      lowerContent.includes('view') ||
      lowerContent.includes('access') ||
      lowerContent.includes('check'))
  ) {
    suggestions.push({
      id: 'view-spaces',
      label: 'Open Knowledge Spaces',
      type: 'navigation',
      action: 'navigate:spaces',
    })
  }
  // Detect analytics/metrics mentions
  if (
    lowerContent.match(/\b(metric|kpi|analytics|performance|data|report)\b/i) &&
    (lowerContent.includes('view') ||
      lowerContent.includes('see') ||
      lowerContent.includes('check') ||
      lowerContent.includes('review'))
  ) {
    suggestions.push({
      id: 'view-analytics',
      label: 'View Analytics',
      type: 'navigation',
      action: 'navigate:analytics',
    })
  }
  // Detect assets mentions
  if (
    lowerContent.match(/\b(asset|file|document|pdf|image|resource)\b/i) &&
    (lowerContent.includes('view') ||
      lowerContent.includes('download') ||
      lowerContent.includes('access') ||
      lowerContent.includes('open'))
  ) {
    suggestions.push({
      id: 'view-assets',
      label: 'View Assets',
      type: 'navigation',
      action: 'navigate:assets',
    })
  }
  // Detect meeting/scheduling mentions
  if (
    lowerContent.match(/\b(meeting|schedule|calendar|appointment|call)\b/i) &&
    (lowerContent.includes('schedule') ||
      lowerContent.includes('set up') ||
      lowerContent.includes('arrange') ||
      lowerContent.includes('book'))
  ) {
    suggestions.push({
      id: 'schedule-meeting',
      label: 'Schedule Meeting',
      type: 'action',
      action: 'schedule:meeting',
    })
  }
  // Detect follow-up questions
  if (
    lowerContent.includes('would you like') ||
    lowerContent.includes('need more') ||
    lowerContent.includes('anything else') ||
    lowerContent.includes('can i help')
  ) {
    suggestions.push({
      id: 'continue-chat',
      label: 'Ask Follow-up',
      type: 'action',
      action: 'chat:focus',
    })
  }
  // Limit to 3 suggestions max, prioritizing by relevance
  return suggestions.slice(0, 3)
}
/**
 * Check if content warrants action suggestions
 */
export function shouldShowActionSuggestions(content: string): boolean {
  // Don't show for very short responses
  if (content.length < 50) return false
  // Don't show for error messages
  if (
    content.toLowerCase().includes('error') ||
    content.toLowerCase().includes('sorry') ||
    content.toLowerCase().includes('unable')
  ) {
    return false
  }
  return true
}
